import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { JsonEditorComponent } from 'ang-jsoneditor';
import { Tenant } from '../../../model/tenant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { TenantsService } from '../../../services/tenants.service';
import { MessageService } from 'primeng/api';
import { ApplicationSubscription } from '../../../model/application-subscription';
import {
  ApplicationSubscriptionPersistRequest
} from '../../../model/requests/application-subscription-persist-request';
import { ApplicationSubscriptionsService } from '../../../services/application-subscriptions.service';
import { JsonEditorService } from '../../../services/json-editor.service';
import { AssetLoaderService } from '../../../services/asset-loader.service';
import { noSpecialCharacters, noWhitespaces } from '../../../shared/custom-validators';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AuthGuard } from '../../../common/auth-guard.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { CommonMessagesService } from '../../../services/common-messages.service';
import { Organization } from "../../../model/organization";
import { v4 as uuidv4 } from 'uuid';
import { LoggedUser } from "../../../common/logged-user.service";

@Component({
  selector: 'app-subscriptions-form',
  templateUrl: './subscriptions-form.component.html',
  styleUrls: ['./subscriptions-form.component.scss'],
  animations: [
    trigger('grow', [
      transition('void <=> *', []),
      transition('* <=> *', [
        style({height: '{{startHeight}}px', opacity: 0}),
        animate('.5s ease'),
      ], {params: {startHeight: 0}})
    ])
  ]
})
export class SubscriptionsFormComponent implements OnInit, ComponentCanDeactivate {

  public subscription: ApplicationSubscription;

  public tenants: Tenant[];
  public organizations: Organization[];
  public subscriptionTypes: any[];
  public protocols: any[];

  public subscriptionForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: {tenantUuid: string, oldName: string, organizationUuid: string};
  public appliedToInfoVisible: boolean;

  public activeOptions = [
    { label: 'applications.subscriptions.enabled', value: true },
    { label: 'applications.subscriptions.disabled', value: false },
  ];

  @ViewChild('appliedToEditor', {static: false})
  appliedToEditor: JsonEditorComponent;

  @ViewChild('protocolConfigEditor', {static: false})
  protocolConfigEditor: JsonEditorComponent;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private cdr: ChangeDetectorRef,
              public jsonEditorService: JsonEditorService,
              private i18n: TranslocoService,
              private applicationSubscriptionsService: ApplicationSubscriptionsService,
              private authGuard: AuthGuard,
              private tenantsService: TenantsService,
              private messageService: MessageService,
              private commonMessagesService: CommonMessagesService,
              private assetLoaderService: AssetLoaderService,
              private loggedUser: LoggedUser
  ) {

    this.subscriptionTypes = [
      {name: 'ALL', configRoute: 'form-config/subscriptions/applied-to/all.json'},
      {name: 'EVENTS', configRoute: 'form-config/subscriptions/applied-to/events.json'},
      {name: 'COMPLEX_EVENTS', configRoute: 'form-config/subscriptions/applied-to/complex_events.json'},
      {name: 'MEASUREMENTS', configRoute: 'form-config/subscriptions/applied-to/measurements.json'},
      {name: 'PROPERTIES', configRoute: 'form-config/subscriptions/applied-to/properties.json'}
    ];

    this.protocols = [
      {name: 'AMQP_SERVER', configRoute: 'form-config/subscriptions/protocol-config/amqp-server.json'},
      {name: 'HTTPS', configRoute: 'form-config/subscriptions/protocol-config/https.json'},
      {name: 'KAFKA', configRoute: 'form-config/subscriptions/protocol-config/kafka.json'},
      {name: 'AZURE_KAFKA_EVENT_HUB', configRoute: 'form-config/subscriptions/protocol-config/azure-kafka-event-hub.json'},
      {name: 'EMQX', configRoute: 'form-config/subscriptions/protocol-config/emqx.json'},
      {name: 'FIWARE_IOT_AGENT_JSON', configRoute: 'form-config/subscriptions/protocol-config/fiware-iot-agent-json.json'},
      {name: 'SENTILO', configRoute: 'form-config/subscriptions/protocol-config/sentilo.json'},
      {name: 'INSTALLED_BROKER_NOTIFICATIONS', configRoute: 'form-config/subscriptions/protocol-config/installed-broker-notifications.json'}
    ];
    if (authGuard.superAdminRequired()) {
      this.protocols.push({
        name: 'INSTALLED_BROKER',
        configRoute: 'form-config/subscriptions/protocol-config/installed-broker.json'
      });
    }
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path);
      }
    });
  }

  loadCreationForm(): void {
    this.subscription = new ApplicationSubscription();
    if (!this.authGuard.superAdminRequired()) {
      this.subscription.tenant = this.tenants[0];
    }
    this.subscription.active = true;
    this.createForm(this.subscription);
  }

  loadEditionForm(tenantUuid, name): void {
    this.tenantsService.getTenantApplicationSubscription(tenantUuid, name).toPromise().then(response => {
      this.subscription = response.applicationSubscription;
      this.tenants = [this.subscription.tenant];
      this.organizations = [this.subscription.organization];
      this.editInfo = {tenantUuid: this.subscription.tenant.uuid, oldName: this.subscription.name, organizationUuid: this.subscription.organization?.uuid};
      this.subscription.appliedTo = JSON.parse(this.subscription.appliedTo);
      this.subscription.protocolConfig = JSON.parse(this.subscription.protocolConfig);
      this.createForm(this.subscription);
    });
  }

  private createForm(subscription: ApplicationSubscription): void {
    const subscriptionType = this.subscriptionTypes.filter(st => st.name === subscription.subscriptionType);
    const protocol = this.protocols.filter(p => p.name === subscription.protocol);

    this.subscriptionForm = this.formBuilder.group({
      name: [subscription.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters, noWhitespaces]],
      description: [subscription.description, [Validators.maxLength(255)]],
      subscriptionType: [subscriptionType.length > 0 ? subscriptionType[0] : null, [Validators.required, Validators.maxLength(255)]],
      appliedTo: [subscription.appliedTo, [Validators.required]],
      protocol: [{value: protocol.length > 0 ? protocol[0] : null, disabled: !subscription.tenant}, [Validators.required, Validators.maxLength(255)]],
      protocolConfig: [subscription.protocolConfig, [Validators.required]],
      tenant: [subscription.tenant, [Validators.required]],
      organization: [subscription.organization ],
      active: [subscription.active ]
    });
    this.subscriptionForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.subscriptionForm.get('tenant').disable();
      this.loggedUser.userObservable.subscribe(user => {
        if (!this.authGuard.superAdminRequired() && user.organizationUuid) {
          const org = {uuid: user.organizations[0].uuid, name: user.organizations[0].name};
          this.organizations = user.organizations;
          this.subscriptionForm.get('organization').setValue(this.organizations[0]);
          this.subscriptionForm.get('organization').disable();
        } else if (this.mode !== 'edit') {
          this.loadOrganizations(user.tenantUuid);
        }
      });

    }

    if (this.mode === 'edit') {
      this.subscriptionForm.get('tenant').disable();
      this.subscriptionForm.get('organization').disable();
      this.assetLoaderService.loadAssetWithRoute(subscriptionType[0].configRoute).toPromise().then(config => {
        this.appliedToEditor.setSchema(config.schema, {});
      });
      this.assetLoaderService.loadAssetWithRoute(protocol[0].configRoute).toPromise().then(config => {
        this.configureProtocolConfigEditor(config);
      });
    }

    this.configureEditorsLanguage();
  }

  private configureEditorsLanguage(): void {
    this.cdr.detectChanges();
    this.i18n.selectTranslate('model.subscription').subscribe(() => {
      const activeLang = this.i18n.getActiveLang();
      this.appliedToEditor.getEditor().language = activeLang;
      this.protocolConfigEditor.getEditor().language = activeLang;
    });
  }

  configureProtocolConfigEditor(config): void {
    this.protocolConfigEditor.setMode("code");
    if (this.subscriptionForm.get('protocol').value.name === 'INSTALLED_BROKER') {
      const tenant = this.subscriptionForm.get('tenant').value.name.toLowerCase();
      const routingKey = config.schema.properties.routingKey;
    } else if (this.subscriptionForm.get('protocol').value.name === 'INSTALLED_BROKER_NOTIFICATIONS') {
      config.template.routingKey=config.template.routingKey.replace('{tenantUuid}',this.subscriptionForm.get('tenant').value.uuid);
      config.template.routingKey=config.template.routingKey.replace('{randomUuid}', uuidv4());
      this.protocolConfigEditor.setMode("view");
    }
    this.protocolConfigEditor.setSchema(config.schema, {});
  }

  subscriptionTypeChanged(event: any): void {
    this.assetLoaderService.loadAssetWithRoute(event.value.configRoute).toPromise().then(config => {
      this.appliedToEditor.setSchema(config.schema, {});
      this.subscriptionForm.get('appliedTo').setValue(config.template);
    });
  }

  protocolChanged(event: any): void {
    this.assetLoaderService.loadAssetWithRoute(event.value.configRoute).toPromise().then(config => {
      this.configureProtocolConfigEditor(config);
      this.subscriptionForm.get('protocolConfig').setValue(config.template);
    });
  }

  tenantChanged(): void {
    this.subscriptionForm.get('protocol').enable();
    this.subscriptionForm.get('organization').enable();
    this.subscriptionForm.get('organization').setValue(null);
    this.tenantsService.getTenantOrganizationSummaries(this.subscriptionForm.get('tenant').value.uuid).toPromise()
      .then(response => {
        this.organizations = response.organizations;
      });


  }

  toggleActive(): void {
    const oldValue = this.subscriptionForm.get('active').value;
    this.subscriptionForm.get('active').setValue(!oldValue);
  }

  isAppliedToValid(): boolean {
    if (this.appliedToEditor.isValidJson()) {
      return this.appliedToEditor.getEditor().validateSchema.errors === null;
    }
    return false;
  }

  isProtocolConfigValid(): boolean {
    if (this.protocolConfigEditor.isValidJson()) {
      return this.protocolConfigEditor.getEditor().validateSchema.errors === null;
    }
    return false;
  }

  persistSubscriptionData(): void {
    if (!this.subscriptionForm.valid) { return; }

    if (!this.isAppliedToValid() || !this.isProtocolConfigValid()) {
      const summary = this.i18n.translate('error.summary.configErrors');
      const detail = this.i18n.translate('error.jsonEditor.configErrors');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.applicationSubscriptionsService.createApplicationSubscription(persistData).toPromise();
    } else {
      promise = this.applicationSubscriptionsService.editApplicationSubscription(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/subscriptions`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): ApplicationSubscriptionPersistRequest {
    const formValue = this.subscriptionForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      subscriptionType: formValue.subscriptionType.name,
      appliedTo: JSON.stringify(formValue.appliedTo),
      protocol: formValue.protocol.name,
      protocolConfig: JSON.stringify(formValue.protocolConfig),
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null,
      organizationUuid: this.mode === 'edit' ? this.editInfo.organizationUuid : formValue.organization?.uuid,
      active: formValue.active
    };
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.subscriptionForm.untouched;
  }

  private loadOrganizations(tenantUuid: string): void {
    this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise()
      .then(response => {
        this.organizations = response.organizations;
      });
  }

}

